import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  X, Search, AlertTriangle,
  FileText, CheckCircle, HelpCircle,
  Star, Check,
} from "lucide-react";
import axiosInstance from "../../api/api";
import bookingApi from "../../api/booking";
import DisputeModal from "../dispute/DisputeModal";
import CancelWarningModal from "./CancelWarningModal";
import HostBillingModal from "./HostBillingModal";
import BookingReviewModal from "../Booking/BookingReviewModal";
import { getPaymentPlanDetails } from "../../utils/paymentPlanUtils";
import { useToast } from "../../context/ToastContext";
import { getCustomerBilling } from "../../api/profile";


/* ─── Helpers ─── */
const fmt = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : "—";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
};


/* ─── Card Status Resolution ─── */
const resolveCardStatus = (booking) => {
  const normLifecycle = String(booking?.lifecycleStatus || "NORMAL").toUpperCase();
  const normFulfillment = String(booking?.fulfillmentStatus || "NOT_STARTED").toUpperCase();
  const normBooking = String(booking?.bookingStatus || "PENDING_PAYMENT").toUpperCase();
  const rawRemaining = booking?.financials?.remainingAmount || 0;
  const offPlatform = booking?.financials?.offPlatformAmount || 0;
  const remainingAmount = Math.abs(rawRemaining - offPlatform) < 0.01 ? 0 : rawRemaining;

  // 1. Highest Priority: Disputed / Cancelled
  if (normLifecycle === "DISPUTED" || normBooking === "DISPUTED") {
    return {
      label: "Disputed",
      valueClass: "text-[#FF4C18] font-bold",
      badgeBg: "bg-white text-[#FF4C18] border border-[#FF4C18]",
      actions: ["help", "view_dispute"],
      key: "disputed"
    };
  }

  if (normLifecycle === "CANCELLED") {
    return {
      label: "Cancelled",
      valueClass: "text-[#FF4C18] font-bold",
      badgeBg: "bg-white text-[#FF4C18] border border-[#FF4C18]",
      actions: ["help"],
      key: "cancelled"
    };
  }

  // 2. Completed: Once bookingStatus is COMPLETED
  if (normBooking === "COMPLETED") {
    const plan = String(booking?.paymentPlan || booking?.plan || "").toUpperCase();
    const isDirect = plan === "DIRECT";
    const hasReviewed = Boolean(booking?.hasReviewed);
    return {
      label: "Completed",
      valueClass: "text-[#489C65] font-bold",
      badgeBg: "bg-white text-[#489C65] border border-[#489C65]",
      actions: hasReviewed
        ? (isDirect ? ["help"] : ["invoice", "help"])
        : (isDirect ? ["write_review", "help"] : ["invoice", "write_review"]),
      key: "completed"
    };
  }

  // 3. In Progress: If pay amount is not zero (payment pending)
  if (remainingAmount > 0 || normBooking === "PARTIALLY_PAID" || normBooking === "PENDING_PAYMENT") {
    return {
      label: "In Progress",
      valueClass: "text-[#F7D200] font-bold",
      badgeBg: "bg-white text-[#F7D200] border border-[#F7D200]",
      actions: ["cancel", "help"],
      key: "in_progress"
    };
  }

  // 4. Fulfillment completed / pending customer approval
  if (normFulfillment === "COMPLETED" || normBooking === "PENDING_APPROVAL") {
    return {
      label: "Pending Approval",
      valueClass: "text-[#4285F4] font-bold",
      badgeBg: "bg-white text-[#4285F4] border border-[#4285F4]",
      actions: ["approve_work", "dispute"],
      key: "pending_approval"
    };
  }

  // 5. Default: Confirmed
  return {
    label: "Confirmed",
    valueClass: "text-[#489C65] font-bold",
    badgeBg: "bg-white text-[#489C65] border border-[#489C65]",
    actions: ["cancel", "help"],
    key: "confirmed"
  };
};

/* ─── Action Button ─── */
const ActionButton = ({
  action,
  booking,
  onOpenDispute,
  onInitiateCancel,
  onRefresh,
  onDownloadInvoice,
  isDownloadingInvoice = false,
  onWriteReview,
}) => {
  const { showSuccess, showError, showInfo } = useToast();
  const outline = "inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-[11px] sm:text-[13px] font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition shadow-2xs";
  const primary = "inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-[11px] sm:text-[13px] font-semibold bg-[#FF4D12] text-white hover:bg-[#e64415] transition shadow-xs";

  const MAP = {
    invoice: (
      <button
        className={outline}
        disabled={isDownloadingInvoice}
        onClick={(e) => {
          e.stopPropagation();
          if (onDownloadInvoice) onDownloadInvoice(booking);
        }}
      >
        {isDownloadingInvoice ? (
          <>
            <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
            <span>Downloading...</span>
          </>
        ) : (
          <>
            <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
            <span>Invoice</span>
          </>
        )}
      </button>
    ),
    cancel: (
      <button className={outline} onClick={() => onInitiateCancel ? onInitiateCancel(booking) : (onOpenDispute && onOpenDispute(booking, "raise"))}>
        <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" /> Cancel
      </button>
    ),
    dispute: (
      <button className={outline} onClick={() => onOpenDispute ? onOpenDispute(booking, "raise") : showInfo("Dispute feature")}>
        <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" /> Dispute
      </button>
    ),
    write_review: (
      <button
        className={primary}
        onClick={(e) => {
          e.stopPropagation();
          if (onWriteReview) {
            onWriteReview(booking);
          }
        }}
      >
        <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Write Review
      </button>
    ),
    approve_work: (
      <button
        className={primary}
        onClick={async (e) => {
          e.stopPropagation();
          try {
            await bookingApi.markCustomerComplete(booking.bookingId || booking._id);
            showSuccess("Work approved! Booking completed.");
            setTimeout(() => {
              if (onRefresh) onRefresh();
              else window.location.reload();
            }, 800);
          } catch (err) {
            showError(err.response?.data?.message || err.message || "Failed to approve work");
          }
        }}
      >
        <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Approve Work
      </button>
    ),
    view_dispute: (
      <button className={outline} onClick={() => onOpenDispute ? onOpenDispute(booking, "view") : showInfo("Dispute details")}>
        <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" /> View Dispute
      </button>
    ),
    help: (
      <button className={outline} onClick={() => (window.location.href = "/help")}>
        <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" /> Help
      </button>
    ),
  };

  return MAP[action] || null;
};

/* ─── Booking Card ─── */
const BookingCard = ({
  booking,
  onOpenDispute,
  onInitiateCancel,
  onRefresh,
  onDownloadInvoice,
  downloadingBookingId,
  isSelected = false,
  onWriteReview,
}) => {
  const [imgError, setImgError] = useState(false);

  const plan       = (booking?.paymentPlan || "").toUpperCase();
  const fin        = booking?.financials || {};
  const prof       = booking?.professionalInfo || {};
  const statusInfo = resolveCardStatus(booking);
  const actions    = statusInfo.actions;
  const isFlexible = plan === "FLEXIBLE";
  const isModerate = plan === "MODERATE";
  const isDirect   = plan === "DIRECT";

  const name        = prof.fullName || prof.companyName || "Professional";
  const avatar      = prof.profilePicture;
  const showAvatar  = avatar && !imgError;

  /* Stats columns differ by plan, with conditional upcoming */
  const statCells = (() => {
    const cells = [];
    cells.push({ label: "Total Amount", value: fmt(fin.totalAmount) });

    if (isFlexible) {
      cells.push({ label: "Milestones", value: booking?.milestoneCount != null ? Number(booking.milestoneCount) + 1 : "—" });
    }

    // Only show Upcoming if greater than zero, not purely off-platform direct payment, and NOT cancelled
    const rawRemaining = fin.remainingAmount || 0;
    const offPlatform = fin.offPlatformAmount || 0;
    const effectiveRemaining = Math.abs(rawRemaining - offPlatform) < 0.01 ? 0 : rawRemaining;
    const isCancelledBooking = 
      String(booking?.lifecycleStatus).toUpperCase() === "CANCELLED" || 
      String(booking?.bookingStatus).toUpperCase() === "CANCELLED";

    if (effectiveRemaining > 0 && !isCancelledBooking) {
      cells.push({ label: "Remaining", value: fmt(effectiveRemaining) });
    }

    cells.push({ label: "On GoPratle", value: fmt(fin.paidAmount) });

    if (isModerate) {
      const directPay = fin.totalAmount != null && fin.paidAmount != null ? Math.max(0, fin.totalAmount - fin.paidAmount) : 0;
      if (directPay > 0) {
        cells.push({ label: "Settled Directly", value: fmt(directPay) });
      }
    } else if (isDirect) {
      const directPay = fin.offPlatformAmount != null ? fin.offPlatformAmount : fin.totalAmount;
      if (directPay > 0) {
        cells.push({ label: "Settled Directly", value: fmt(directPay) });
      }
    }

    cells.push({
      label: "Status",
      value: statusInfo.label,
      valueClass: statusInfo.valueClass,
    });

    return cells;
  })();

  return (
    <div
      className={`bg-white rounded-xl sm:rounded-2xl border shadow-[0_1px_6px_rgba(0,0,0,0.04)] overflow-hidden p-3 sm:p-5 transition-all ${
        isSelected
          ? "border-[#FF4D12] ring-1 ring-[#FF4D12]/40 bg-orange-50/15"
          : "border-gray-200"
      }`}
    >
      {/* ── Top Row: Profile + Meta + Plan Badge ── */}
      <div className="flex items-start sm:items-center justify-between gap-2 sm:gap-3 mb-2.5 sm:mb-4">
        {/* Left: Avatar + Name + Desktop Divider & Meta */}
        <div className="flex items-center gap-2 sm:gap-3.5 min-w-0 flex-1 overflow-hidden">
          {/* Avatar */}
          <div className="shrink-0">
            {showAvatar ? (
              <img
                src={avatar}
                alt={name}
                onError={() => setImgError(true)}
                className="w-8 h-8 sm:w-12 sm:h-12 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-orange-50 border border-orange-200 flex items-center justify-center text-[#FF4D12] font-bold text-xs sm:text-lg shrink-0">
                {name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Name */}
          <div className="min-w-0 shrink-0 sm:shrink max-w-[120px] xs:max-w-[150px] sm:max-w-none">
            <h4 className="font-extrabold text-gray-950 text-xs sm:text-[17px] leading-tight truncate">
              {name}
            </h4>
          </div>

          {/* Vertical Divider (Desktop Only) */}
          <div className="hidden sm:block h-8 w-px bg-gray-200 mx-1 shrink-0" />

          {/* Reference ID & Date & Time (Desktop Only) */}
          <div className="hidden sm:flex items-center gap-2 text-[13px] text-gray-800 font-medium whitespace-nowrap overflow-hidden">
            <span>#{booking?.referenceId}</span>
            {booking?.createdAt && (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D12] inline-block shrink-0" />
                <span className="truncate">{formatDate(booking.createdAt)}</span>
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D12] inline-block shrink-0" />
                <span>{formatTime(booking.createdAt)}</span>
              </>
            )}
          </div>
        </div>

        {/* Right: Plan Badge + Mobile Stacked Reference ID */}
        {(() => {
          const { label: planLabel, icon: planIcon } = getPaymentPlanDetails(plan);
          return (
            <div className="flex flex-col items-end gap-1 shrink-0 sm:block">
              <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-[#FF4D12] text-white text-[9px] sm:text-[13px] font-bold px-2 py-1 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl shadow-xs whitespace-nowrap">
                <img src={planIcon} alt={planLabel} className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 object-contain shrink-0" />
                {planLabel}
              </span>
              {/* Booking ID only below badge on mobile view */}
              <div className="sm:hidden text-right leading-tight mt-0.5">
                <span className="text-[10px] font-bold text-gray-900 block">#{booking?.referenceId}</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Metrics Box (Horizontal with dividers) ── */}
      <div className="border border-gray-200 rounded-lg sm:rounded-xl flex items-center divide-x divide-gray-200 overflow-x-auto sm:overflow-hidden bg-white mb-2.5 sm:mb-4 hide-scrollbar">
        {statCells.map((cell) => (
          <div key={cell.label} className="flex-1 px-2.5 py-2 sm:px-3.5 sm:py-3 min-w-[88px] sm:min-w-0 text-left shrink-0 sm:shrink">
            <p className="text-[10px] sm:text-xs text-gray-600 font-medium mb-0.5 sm:mb-1 whitespace-nowrap sm:truncate">{cell.label}</p>
            <p className={`text-[11.5px] sm:text-[15px] font-extrabold whitespace-nowrap sm:truncate ${cell.valueClass || "text-gray-950"}`}>
              {cell.value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Actions Row ── */}
      <div
        className="flex flex-wrap gap-1.5 sm:gap-2.5"
        onClick={(e) => e.stopPropagation()}
      >
        {actions.map((a) => (
          <ActionButton
            key={a}
            action={a}
            booking={booking}
            onOpenDispute={onOpenDispute}
            onInitiateCancel={onInitiateCancel}
            onRefresh={onRefresh}
            onDownloadInvoice={onDownloadInvoice}
            isDownloadingInvoice={downloadingBookingId === (booking?.bookingId || booking?._id)}
            onWriteReview={onWriteReview}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── Tab config ─── */
const TABS = [
  { key: "all",         label: "All Bookings" },
  { key: "confirmed",   label: "Confirmed" },
  { key: "pending",     label: "Pending Approval" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed",   label: "Completed" },
  { key: "dispute",     label: "Disputes" },
  { key: "cancelled",   label: "Cancelled" },
];

const matchesTab = (b, tab) => {
  if (tab === "all") return true;
  const s = resolveCardStatus(b).key;
  if (tab === "confirmed") return s === "confirmed";
  if (tab === "pending") return s === "pending_approval";
  if (tab === "in_progress") return s === "in_progress";
  if (tab === "completed") return s === "completed";
  if (tab === "dispute") return s === "disputed";
  if (tab === "cancelled") return s === "cancelled";
  return true;
};

/* ─── Main Modal ─── */
export default function BookingLogModal({ isOpen, onClose, eventId }) {
  const { showSuccess, showError } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [search,   setSearch]   = useState("");
  const [tab,      setTab]      = useState("all");

  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);

  const [cancelWarningOpen, setCancelWarningOpen] = useState(false);
  const [disputeModalMode, setDisputeModalMode] = useState("standard");
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [disputeModalView, setDisputeModalView] = useState("raise");
  const [selectedDisputeBooking, setSelectedDisputeBooking] = useState(null);

  const [isDownloadingInvoices, setIsDownloadingInvoices] = useState(false);
  const [downloadingBookingId, setDownloadingBookingId] = useState(null);
  const [reviewModalBooking, setReviewModalBooking] = useState(null);
  const [eventName, setEventName] = useState("");
  const [pendingInvoiceAction, setPendingInvoiceAction] = useState(null);
  const [hostBillingModalOpen, setHostBillingModalOpen] = useState(false);
  const [hostBillingDetails, setHostBillingDetails] = useState(null);

  const eligibleInvoiceBookings = useMemo(() => {
    return bookings.filter((b) => {
      const normBooking = String(b.bookingStatus || "").toUpperCase();
      const normLifecycle = String(b.lifecycleStatus || "NORMAL").toUpperCase();
      const plan = String(b.paymentPlan || b.plan || "").toUpperCase();
      const isDirect = plan === "DIRECT";
      return normBooking === "COMPLETED" && normLifecycle === "NORMAL" && !isDirect;
    });
  }, [bookings]);

  const isBillingComplete = (details) => {
    return Boolean(
      details &&
      details.billingName &&
      details.billingName.trim().length >= 2 &&
      details.address &&
      details.address.trim().length >= 5 &&
      details.state &&
      details.state.trim().length > 0 &&
      details.pincode &&
      /^[1-9][0-9]{5}$/.test(details.pincode.trim())
    );
  };

  const executeSingleInvoiceDownload = async (booking) => {
    const bId = booking?.bookingId || booking?._id;
    if (!bId) {
      showError("Booking ID not found.");
      return;
    }
    try {
      setDownloadingBookingId(bId);
      const res = await bookingApi.downloadHostInvoicePdf(bId);
      let filename = `Invoice_${booking?.hostInvoiceNumber || booking?.referenceId || bId}.pdf`;
      const disposition = res.headers?.["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showSuccess(`Invoice ${booking?.referenceId ? `#${booking.referenceId}` : ""} downloaded successfully!`);
    } catch (err) {
      console.error("Invoice download error:", err);
      let errCode = null;
      let errMsg = "Failed to download invoice";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errCode = parsed.code;
          errMsg = parsed.message || errMsg;
        } catch (_) {}
      } else if (err.response?.data) {
        errCode = err.response.data.code;
        errMsg = err.response.data.message || errMsg;
      }

      if (errCode === "PROFESSIONAL_KYC_REQUIRED") {
        showError("Professional haven't submitted their address yet, the download invoice is available once they submit it.");
        return;
      }
      showError(errMsg);
    } finally {
      setDownloadingBookingId(null);
    }
  };

  const handleDownloadInvoiceClick = async (booking) => {
    try {
      const billingRes = await getCustomerBilling();
      const bDetails = billingRes?.billingDetails;
      setHostBillingDetails(bDetails);

      if (isBillingComplete(bDetails)) {
        await executeSingleInvoiceDownload(booking);
      } else {
        setPendingInvoiceAction({ type: "single", booking });
        setHostBillingModalOpen(true);
      }
    } catch (err) {
      console.error("Error checking billing details:", err);
      setPendingInvoiceAction({ type: "single", booking });
      setHostBillingModalOpen(true);
    }
  };

  const executeAllInvoicesDownload = async () => {
    const eligibleBookings = eligibleInvoiceBookings;
    if (eligibleBookings.length === 0) {
      showError("No invoices available for download. Invoices are generated for completed bookings.");
      return;
    }

    try {
      setIsDownloadingInvoices(true);
      let successCount = 0;
      let kycPendingCount = 0;

      for (let i = 0; i < eligibleBookings.length; i++) {
        const b = eligibleBookings[i];
        const bId = b.bookingId || b._id;
        try {
          const res = await bookingApi.downloadHostInvoicePdf(bId);
          let filename = `Invoice_${b.hostInvoiceNumber || b.referenceId || bId}.pdf`;
          const disposition = res.headers?.["content-disposition"];
          if (disposition && disposition.includes("filename=")) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          const blob = new Blob([res.data], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          successCount++;
        } catch (err) {
          console.error(`Failed to download invoice for booking ${bId}:`, err);
          let errCode = null;
          if (err.response?.data instanceof Blob) {
            try {
              const text = await err.response.data.text();
              const parsed = JSON.parse(text);
              errCode = parsed.code;
            } catch (_) {}
          } else if (err.response?.data) {
            errCode = err.response.data.code;
          }

          if (errCode === "PROFESSIONAL_KYC_REQUIRED") {
            kycPendingCount++;
          }
        }
        if (i < eligibleBookings.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      if (kycPendingCount > 0) {
        showError("Some professionals haven't submitted their KYC yet; once submitted, their invoices will also be available for download.");
      }

      if (successCount > 0) {
        showSuccess(`Downloaded ${successCount} invoice${successCount > 1 ? "s" : ""} successfully!`);
      } else if (kycPendingCount === 0) {
        showError("Failed to download invoices");
      }
    } catch (err) {
      console.error("All invoices download error:", err);
      showError("Failed to download all invoices");
    } finally {
      setIsDownloadingInvoices(false);
    }
  };

  const handleDownloadAllInvoicesClick = async () => {
    if (isDownloadingInvoices) return;

    const eligibleBookings = eligibleInvoiceBookings;
    if (eligibleBookings.length === 0) {
      showError(
        "Invoices are available once at least one booking is Completed."
      );
      return;
    }

    try {
      setIsDownloadingInvoices(true);
      const billingRes = await getCustomerBilling();
      const bDetails = billingRes?.billingDetails;
      setHostBillingDetails(bDetails);

      if (isBillingComplete(bDetails)) {
        await executeAllInvoicesDownload();
      } else {
        setIsDownloadingInvoices(false);
        setPendingInvoiceAction({ type: "all" });
        setHostBillingModalOpen(true);
      }
    } catch (err) {
      console.error("Error checking billing details:", err);
      setIsDownloadingInvoices(false);
      setPendingInvoiceAction({ type: "all" });
      setHostBillingModalOpen(true);
    }
  };


  const handleInitiateCancel = (booking) => {
    setSelectedDisputeBooking(booking);
    setCancelWarningOpen(true);
  };

  const handleConfirmCancelWarning = () => {
    setCancelWarningOpen(false);
    setDisputeModalMode("cancellation");
    setDisputeModalView("raise");
    setDisputeModalOpen(true);
  };

  const handleOpenDispute = (booking, initialView = "raise") => {
    setSelectedDisputeBooking(booking);
    setDisputeModalMode("standard");
    setDisputeModalView(initialView);
    setDisputeModalOpen(true);
  };

  const fetchBookings = useCallback(async () => {
    if (!eventId) return;
    try {
      setLoading(true); setError("");
      const res = await axiosInstance.get(`/bookings/customer/event/${eventId}`);
      setBookings(res.data?.data?.bookings || []);
      setEventName(res.data?.data?.eventName || "");
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load booking log";
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [eventId, showError]);

  useEffect(() => {
    if (isOpen) {
      fetchBookings();
      setSearch("");
      setTab("all");
      setSelectedBookingIds([]);
    }
  }, [isOpen, fetchBookings]);

  useEffect(() => {
    setSelectedBookingIds([]);
  }, [tab, search]);

  const filtered = bookings.filter((b) => {
    const q = search.toLowerCase();
    const statusInfo = resolveCardStatus(b);
    return (
      (!q ||
        (b.professionalInfo?.fullName || "").toLowerCase().includes(q) ||
        (b.referenceId || "").toLowerCase().includes(q) ||
        statusInfo.label.toLowerCase().includes(q) ||
        (b.bookingStatus || "").toLowerCase().includes(q)) &&
      matchesTab(b, tab)
    );
  });

  const countFor = (key) =>
    key === "all" ? bookings.length : bookings.filter((b) => matchesTab(b, key)).length;

  const pendingBookings = useMemo(() => {
    return filtered.filter(
      (b) => resolveCardStatus(b).key === "pending_approval"
    );
  }, [filtered]);

  const toggleSelectBooking = (id) => {
    if (!id) return;
    setSelectedBookingIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleBulkApprove = async () => {
    if (selectedBookingIds.length === 0 || isBulkApproving) return;

    try {
      setIsBulkApproving(true);
      const res = await bookingApi.markCustomerComplete(selectedBookingIds);
      const count = res.data?.data?.completedCount || selectedBookingIds.length;
      showSuccess(
        count === 1
          ? "Work approved! Booking completed."
          : `Work approved for ${count} professionals! Bookings completed.`
      );
      setSelectedBookingIds([]);
      setTimeout(() => {
        fetchBookings();
      }, 800);
    } catch (err) {
      showError(
        err.response?.data?.message ||
          err.message ||
          "Failed to approve selected work"
      );
    } finally {
      setIsBulkApproving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[800]" onClick={onClose} />

      {/* Panel */}
      <div className="fixed inset-0 z-[801] flex items-center justify-center p-2 sm:p-6">
        <div
          className="bg-white rounded-[20px] sm:rounded-[24px] shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden h-[85vh] sm:h-[88vh] min-h-[480px] sm:min-h-[550px] max-h-[850px]"
          style={{ animation: "blModalIn 0.2s ease" }}
        >
          {/* Header */}
          <div className="flex items-start justify-between px-4 pt-4 pb-3 sm:px-6 sm:pt-6 sm:pb-4 border-b border-gray-100 shrink-0">
            <div>
              <h2 className="text-[16px] sm:text-[18px] font-extrabold text-gray-900 tracking-tight">Booking Log</h2>
              <p className="text-[11px] sm:text-[12px] text-gray-400 mt-0.5 font-medium">
                View and manage all your bookings in one place.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-800 transition shrink-0"
              >
                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-4 py-2 sm:px-6 sm:py-3 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name, booking ID or status…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-8 sm:pl-9 pr-3 sm:pr-4 h-8 sm:h-9 rounded-lg sm:rounded-xl border border-gray-200 bg-gray-50 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#FF4D12]/30 focus:border-[#FF4D12]/50 placeholder:text-gray-400"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="px-4 sm:px-6 border-b border-gray-100 shrink-0 overflow-x-auto bl-no-scroll">
            <div className="flex gap-4 sm:gap-6 min-w-max">
              {TABS.map((t) => {
                const count = countFor(t.key);
                const active = tab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setTab(t.key)}
                    className={`flex items-center gap-1.5 pb-2.5 pt-1 text-xs sm:text-[13px] font-bold border-b-2 transition relative whitespace-nowrap ${
                      active
                        ? "text-[#FF4D12] border-[#FF4D12]"
                        : "text-gray-500 hover:text-gray-800 border-transparent"
                    }`}
                  >
                    <span>{t.label}</span>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.2 rounded-full ${
                        active
                          ? "bg-orange-100 text-[#FF4D12]"
                          : "bg-gray-100 text-gray-400"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* List Area */}
          <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4 bl-scrollbar flex flex-col">
            {/* Bulk Approval Bar (Pending Approval Tab) */}
            {tab === "pending" && pendingBookings.length > 0 && (
              <div className="flex items-center justify-between gap-3 bg-gradient-to-r from-orange-50/90 to-amber-50/70 border border-orange-200/80 rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 mb-3 sm:mb-4 shrink-0 shadow-2xs">
                <span className="text-xs sm:text-[13px] font-bold text-gray-800">
                  {selectedBookingIds.length > 0
                    ? `${selectedBookingIds.length} ${
                        selectedBookingIds.length === 1
                          ? "professional selected"
                          : "professionals selected"
                      }`
                    : "Select professionals to approve"}
                </span>

                <button
                  disabled={selectedBookingIds.length === 0 || isBulkApproving}
                  onClick={handleBulkApprove}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-[13px] font-bold transition-all shadow-xs ${
                    selectedBookingIds.length > 0 && !isBulkApproving
                      ? "bg-[#FF4D12] text-white hover:bg-[#e64415] active:scale-95 cursor-pointer"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {isBulkApproving
                    ? "Approving..."
                    : `Approve Selected Work (${selectedBookingIds.length})`}
                </button>
              </div>
            )}

            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-9 h-9 border-4 border-[#FF4D12] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-400 font-medium">Loading bookings…</p>
              </div>
            ) : error ? (
              <div className="flex-1 flex flex-col items-center justify-center py-12 text-center">
                <p className="text-sm text-red-500 font-semibold">{error}</p>
                <button onClick={fetchBookings} className="mt-3 text-xs text-[#FF4D12] font-bold underline underline-offset-2">
                  Retry
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 mx-auto bg-orange-50 rounded-full flex items-center justify-center mb-3">
                  <FileText className="w-7 h-7 text-[#FF4D12]/50" />
                </div>
                <p className="text-sm font-semibold text-gray-600">No bookings found</p>
                <p className="text-xs text-gray-400 mt-1">
                  {search ? "Try a different search term" : "No bookings in this category"}
                </p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {filtered.map((b) => {
                  const bId = b.bookingId || b._id;
                  const isPending = resolveCardStatus(b).key === "pending_approval";
                  const showCheckbox = tab === "pending" && isPending;
                  const isSelected = selectedBookingIds.includes(bId);

                  return (
                    <div
                      key={bId || b.referenceId}
                      className={showCheckbox ? "flex items-center gap-2 sm:gap-3 w-full" : ""}
                    >
                      {showCheckbox && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelectBooking(bId);
                          }}
                          className="shrink-0 flex items-center justify-center cursor-pointer p-0.5 sm:p-1 group select-none"
                          title={isSelected ? "Unselect" : "Select professional to approve work"}
                        >
                          <div
                            className={`w-4 h-4 sm:w-5 sm:h-5 rounded-[5px] sm:rounded-md border-2 flex items-center justify-center transition-all ${
                              isSelected
                                ? "bg-[#FF4D12] border-[#FF4D12] text-white shadow-xs scale-105"
                                : "border-gray-300 group-hover:border-[#FF4D12] bg-white"
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 stroke-[3.5]" />}
                          </div>
                        </div>
                      )}

                      <div className={showCheckbox ? "flex-1 min-w-0" : ""}>
                        <BookingCard
                          booking={b}
                          onOpenDispute={handleOpenDispute}
                          onInitiateCancel={handleInitiateCancel}
                          onRefresh={fetchBookings}
                          onDownloadInvoice={handleDownloadInvoiceClick}
                          downloadingBookingId={downloadingBookingId}
                          isSelected={isSelected}
                          onWriteReview={(booking) => setReviewModalBooking(booking)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 sm:px-6 sm:py-3 border-t border-gray-50 shrink-0 text-center">
            <p className="text-[10px] text-gray-400 font-medium">
              Payouts are released only after the event is completed and approved.
            </p>
          </div>
        </div>
      </div>

      <CancelWarningModal
        isOpen={cancelWarningOpen}
        onClose={() => setCancelWarningOpen(false)}
        onConfirm={handleConfirmCancelWarning}
      />

      {/* Dispute Modal */}
      <DisputeModal
        isOpen={disputeModalOpen}
        onClose={() => setDisputeModalOpen(false)}
        initialView={disputeModalView}
        mode={disputeModalMode}
        bookingId={selectedDisputeBooking?.bookingId || selectedDisputeBooking?._id}
        bookingData={{
          bookingId: selectedDisputeBooking?.bookingId || selectedDisputeBooking?._id,
          referenceId: selectedDisputeBooking?.referenceId || selectedDisputeBooking?.bookingId || "4004556",
          userName: selectedDisputeBooking?.professionalInfo?.fullName || selectedDisputeBooking?.professionalInfo?.companyName || "Rahul Shah",
          location: "Delhi",
          createdAt: selectedDisputeBooking?.createdAt,
        }}
      />

      {/* Host Billing & Tax Modal */}
      <HostBillingModal
        isOpen={hostBillingModalOpen}
        onClose={() => {
          setHostBillingModalOpen(false);
          setPendingInvoiceAction(null);
        }}
        initialBillingDetails={hostBillingDetails}
        onSuccess={async (updatedDetails) => {
          setHostBillingDetails(updatedDetails);
          setHostBillingModalOpen(false);
          if (pendingInvoiceAction?.type === "all") {
            await executeAllInvoicesDownload();
          } else if (pendingInvoiceAction?.type === "single" && pendingInvoiceAction.booking) {
            await executeSingleInvoiceDownload(pendingInvoiceAction.booking);
          }
          setPendingInvoiceAction(null);
        }}
      />

      {reviewModalBooking && (
        <BookingReviewModal
          isOpen={Boolean(reviewModalBooking)}
          onClose={() => setReviewModalBooking(null)}
          bookingId={reviewModalBooking.bookingId || reviewModalBooking._id}
          professionalId={reviewModalBooking.professionalInfo?.professionalId || reviewModalBooking.professionalId}
          professionalName={reviewModalBooking.professionalInfo?.fullName || reviewModalBooking.professionalInfo?.companyName || "Professional"}
          professionalImage={reviewModalBooking.professionalInfo?.profilePicture}
          eventName={reviewModalBooking.eventName || reviewModalBooking.eventTitle || eventName || "Event"}
          onSuccess={() => {
            reviewModalBooking.hasReviewed = true;
            fetchBookings();
          }}
        />
      )}

      <style>{`

        @keyframes blModalIn {
          from { opacity: 0; transform: scale(0.96) translateY(10px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
        /* Orange scrollbar for booking log list */
        .bl-scrollbar::-webkit-scrollbar       { width: 4px; }
        .bl-scrollbar::-webkit-scrollbar-track { background: #fff0eb; border-radius: 99px; }
        .bl-scrollbar::-webkit-scrollbar-thumb { background: #FF4D12; border-radius: 99px; }
        .bl-scrollbar                           { scrollbar-width: thin; scrollbar-color: #FF4D12 #fff0eb; }
        /* Hide scrollbar on tab strip */
        .bl-no-scroll::-webkit-scrollbar { display: none; }
        .bl-no-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </>
  );
}
